<script setup lang="ts">
/**
 * 业务落地页横幅：通过浅色编辑式分栏统一结构，并为不同业务保留独立视觉识别。
 * 插画为装饰性内容；业务信息始终由可访问的正文和卖点列表表达。
 */
type BannerTheme = 'shop' | 'repair' | 'sell'
type BannerVariant = 'catalog' | 'service'

interface TrustBadge {
  icon: string
  label: string
}

interface Props {
  theme: BannerTheme
  variant?: BannerVariant
  homeLabel: string
  currentLabel: string
  parentLabel?: string
  parentTo?: string
  homeTo: string
  badge: string
  title: string
  titleHighlight?: string
  subtitle: string
  heroIcon: string
  visualSrc: string
  trustBadges: TrustBadge[]
  ctaLabel?: string
  ctaTo?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'catalog',
  titleHighlight: '',
  ctaLabel: '',
  ctaTo: '',
  parentLabel: '',
  parentTo: '',
})

const headingId = useId()

const themeStyles: Record<BannerTheme, {
  surface: string
  eyebrow: string
  eyebrowIcon: string
  highlight: string
  visualSurface: string
  trustIcon: string
  cta: string
}> = {
  shop: {
    surface: 'border-emerald-100 bg-gradient-to-br from-white via-emerald-50/75 to-teal-100/70 dark:border-emerald-900/60 dark:from-gray-900 dark:via-emerald-950/70 dark:to-teal-950/70',
    eyebrow: 'bg-emerald-100/90 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/60 dark:text-emerald-200 dark:ring-emerald-800',
    eyebrowIcon: 'text-emerald-700 dark:text-emerald-300',
    highlight: 'text-emerald-700 dark:text-emerald-300',
    visualSurface: 'bg-emerald-200/55 dark:bg-emerald-900/45',
    trustIcon: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300',
    cta: 'bg-emerald-700 text-white hover:bg-emerald-800 focus-visible:ring-emerald-600 dark:bg-emerald-500 dark:text-emerald-950 dark:hover:bg-emerald-400',
  },
  repair: {
    surface: 'border-sky-100 bg-gradient-to-br from-white via-sky-50/80 to-indigo-100/70 dark:border-sky-900/60 dark:from-gray-900 dark:via-slate-900 dark:to-indigo-950/70',
    eyebrow: 'bg-sky-100/90 text-sky-800 ring-sky-200 dark:bg-sky-900/60 dark:text-sky-200 dark:ring-sky-800',
    eyebrowIcon: 'text-sky-700 dark:text-sky-300',
    highlight: 'text-sky-700 dark:text-sky-300',
    visualSurface: 'bg-sky-200/60 dark:bg-sky-900/45',
    trustIcon: 'bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300',
    cta: 'bg-sky-700 text-white hover:bg-sky-800 focus-visible:ring-sky-600 dark:bg-sky-500 dark:text-sky-950 dark:hover:bg-sky-400',
  },
  sell: {
    surface: 'border-lime-100 bg-gradient-to-br from-white via-lime-50/75 to-emerald-100/75 dark:border-emerald-900/60 dark:from-gray-900 dark:via-emerald-950/65 dark:to-lime-950/50',
    eyebrow: 'bg-lime-100/90 text-lime-900 ring-lime-200 dark:bg-lime-900/50 dark:text-lime-200 dark:ring-lime-800',
    eyebrowIcon: 'text-lime-700 dark:text-lime-300',
    highlight: 'text-emerald-700 dark:text-lime-300',
    visualSurface: 'bg-lime-200/60 dark:bg-lime-900/40',
    trustIcon: 'bg-lime-100 text-lime-800 dark:bg-lime-900/50 dark:text-lime-300',
    cta: 'bg-emerald-700 text-white hover:bg-emerald-800 focus-visible:ring-emerald-600 dark:bg-lime-400 dark:text-emerald-950 dark:hover:bg-lime-300',
  },
}

const styles = computed(() => themeStyles[props.theme])
const hasCta = computed(() => props.variant === 'service' && props.ctaLabel && props.ctaTo)
</script>

<template>
  <div data-testid="page-hero-banner">
    <nav class="mb-3 flex items-center gap-2 px-1 text-sm text-gray-500 dark:text-gray-400" :aria-label="currentLabel">
      <NuxtLink
        :to="homeTo"
        class="rounded-md transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:text-white"
      >
        {{ homeLabel }}
      </NuxtLink>
      <UIcon name="i-lucide-chevron-right" class="size-3.5" aria-hidden="true" />
      <NuxtLink
        v-if="parentLabel && parentTo"
        :to="parentTo"
        class="rounded-md transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:text-white"
      >
        {{ parentLabel }}
      </NuxtLink>
      <UIcon v-if="parentLabel && parentTo" name="i-lucide-chevron-right" class="size-3.5" aria-hidden="true" />
      <span class="font-medium text-gray-800 dark:text-gray-200" aria-current="page">{{ currentLabel }}</span>
    </nav>

    <section
      class="relative isolate overflow-hidden rounded-[1.75rem] border shadow-sm"
      :class="styles.surface"
      :aria-labelledby="headingId"
    >
      <div class="grid lg:min-h-[190px] lg:grid-cols-[minmax(0,1.18fr)_minmax(0,0.82fr)]">
        <div class="relative z-10 flex flex-col justify-center px-6 pb-4 pt-6 sm:px-8 lg:px-10 lg:py-5">
          <div
            class="mb-3 inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold tracking-wide ring-1"
            :class="styles.eyebrow"
          >
            <UIcon :name="heroIcon" class="size-4" :class="styles.eyebrowIcon" aria-hidden="true" />
            {{ badge }}
          </div>

          <h1 :id="headingId" class="max-w-3xl text-3xl font-bold leading-[1.12] tracking-tight text-gray-950 sm:text-[2rem] dark:text-white">
            {{ title }}
            <span v-if="titleHighlight" class="block sm:inline" :class="styles.highlight">
              {{ titleHighlight }}
            </span>
          </h1>
          <p class="mt-2 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base dark:text-gray-300">
            {{ subtitle }}
          </p>

          <a
            v-if="hasCta"
            :href="ctaTo"
            class="mt-3 inline-flex min-h-11 w-fit items-center justify-center gap-2 rounded-xl px-5 py-2 text-sm font-semibold shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            :class="styles.cta"
          >
            {{ ctaLabel }}
            <UIcon name="i-lucide-arrow-down" class="size-4" aria-hidden="true" />
          </a>
        </div>

        <div class="relative flex min-h-28 items-center justify-center overflow-hidden px-5 pb-3 sm:min-h-40 sm:px-10 lg:min-h-0 lg:px-7 lg:py-2" aria-hidden="true">
          <div class="absolute inset-x-8 bottom-3 top-3 rounded-[45%] blur-2xl" :class="styles.visualSurface" />
          <img
            :src="visualSrc"
            alt=""
            width="1619"
            height="971"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            class="relative h-28 w-full object-contain object-center sm:h-40 lg:h-[174px]"
          >
        </div>
      </div>

      <ul class="grid grid-cols-3 border-t border-gray-900/[0.07] bg-white/55 dark:border-white/10 dark:bg-gray-950/25" role="list">
        <li
          v-for="(item, index) in trustBadges"
          :key="item.label"
          class="flex min-h-16 flex-col items-center justify-center gap-1 px-2 py-2 text-center text-xs font-medium leading-4 text-gray-700 sm:min-h-12 sm:flex-row sm:gap-2.5 sm:px-4 sm:py-2.5 sm:text-left sm:text-sm dark:text-gray-200"
          :class="index > 0 ? 'border-l border-gray-900/[0.06] dark:border-white/10' : ''"
        >
          <span class="flex size-7 shrink-0 items-center justify-center rounded-lg" :class="styles.trustIcon">
            <UIcon :name="item.icon" class="size-4" aria-hidden="true" />
          </span>
          <span class="sm:leading-5">{{ item.label }}</span>
        </li>
      </ul>
    </section>
  </div>
</template>
